// queue.service — transisi status (port alur prototype). Hanya pakai kolom yang ada di skema.
import { currentUser } from '@core/session';
import { todayISO, genNo, genToken } from '@domain/format';
import { garansiExpired } from '@domain/garansi';
import { isPiutangCara } from '@domain/money';
import { readyDaysSince, storageFeeAmount } from '@domain/storagefee';
import { pushNotif } from '@core/notif';
import { kebijakan } from '@core/settings';
import { logAudit } from '@core/audit';
import { canAdvance } from '@domain/status';
import { canMarkLunas } from '@domain/access';
import type { TicketStatus } from '@core/types';
import * as repo from './queue.repo';

/** Guard transisi RBAC (P1-c): tiket harus di status `from` + peran berwenang (canAdvance). Cegah lompat matriks status via API. */
async function assertAdvance(no: string, from: TicketStatus): Promise<{ status: string; teknisi: string | null; outlet: string | null; kerja: string | null }> {
  const u = currentUser();
  if (!u) throw new Error('Sesi tidak valid.');
  const g = await repo.guardInfo(no);
  if (g.status !== from) throw new Error(`Transisi tidak valid — tiket sudah berstatus "${g.status}".`);
  if (!canAdvance(from, u.role, g.teknisi === u.nama)) throw new Error('Anda tidak berwenang memproses transisi ini.');
  return g;
}

const storageFeeNow = async (no: string): Promise<{ readyDays: number; fee: number }> => {
  const readyDays = readyDaysSince(await repo.siapSinceISO(no));
  const k = kebijakan();
  return { readyDays, fee: storageFeeAmount(readyDays, k.biayaSimpanBulan, k.biayaSimpanOn, { mulaiHari: k.simpanMulaiHari, lepasHari: k.simpanLepasHari, intervalHari: k.simpanIntervalHari }) };
};

const by = (): string => currentUser()?.nama ?? '';
const rp = (n: number): string => 'Rp ' + n.toLocaleString('id-ID');

/**
 * Notif ke aktor berikutnya (best-effort; scoping cabang dari ticketMeta).
 * `tahap = status` membuat notif hilang sendiri saat tiket maju (fitur lonceng). TAPI tugas
 * FINANSIAL (tagih tempo, verifikasi, refund) TIDAK selesai karena status berubah — hutangnya
 * beku di sesi lama saat servis-ulang. Untuk itu `pick` boleh mengembalikan `tahap:null` supaya
 * notifnya tetap ada sampai ditandai dibaca (audit C6).
 */
async function notifyNext(no: string, pick: (m: { outlet: string | null; teknisi: string | null; status: string }) => { target: string; text: string; tahap?: string | null } | null): Promise<void> {
  try { const m = await repo.ticketMeta(no); const r = pick(m); if (r) await pushNotif(r.target, m.outlet, r.text, no, r.tahap === undefined ? m.status : r.tahap); }
  catch { /* notif best-effort */ }
}

/** Diterima → Diagnosa (tugaskan teknisi). Notif → teknisi. */
export async function assignTeknisi(no: string, teknisi: string, catatan: string): Promise<void> {
  await assertAdvance(no, 'Diterima');
  await repo.advance(no, { teknisi }, 'Diagnosa', `Ditugaskan ke ${teknisi}${catatan ? ': ' + catatan : ''}`, by());
  await notifyNext(no, () => ({ target: 'TEK:' + teknisi, text: `🔧 Tiket ${no} ditugaskan ke Anda untuk diagnosa.` }));
}

/** Diagnosa → Menunggu Persetujuan (estimasi). Notif → CS. */
export async function diagnose(no: string, estBiaya: number, lama: string, ket: string): Promise<void> {
  await assertAdvance(no, 'Diagnosa');
  await repo.advance(no, { est_biaya: estBiaya, lama, ket }, 'Menunggu Persetujuan', `Diagnosa · estimasi Rp ${estBiaya.toLocaleString('id-ID')}`, by());
  await notifyNext(no, () => ({ target: 'CS', text: `⏳ ${no} menunggu persetujuan — estimasi ${rp(estBiaya)}. Konfirmasi ke pelanggan.` }));
}

/** Diagnosa → Siap Diambil (tidak bisa / batal). Notif → CS. */
export async function diagnoseCancel(no: string, note: string): Promise<void> {
  await assertAdvance(no, 'Diagnosa');
  await repo.advance(no, { hasil: 'batal', kondisi: 'Dibatalkan (tidak bisa diperbaiki)' }, 'Siap Diambil', `Batal di diagnosa: ${note}`, by());
  await notifyNext(no, () => ({ target: 'CS', text: `❌ ${no} dibatalkan di diagnosa: ${note}. Hubungi pelanggan.` }));
}

/** Menunggu Persetujuan → Dikerjakan (setuju) / Siap Diambil (tidak setuju). Notif → teknisi / CS. */
export async function approve(no: string, setuju: boolean, ket: string): Promise<void> {
  const g = await assertAdvance(no, 'Menunggu Persetujuan');
  if (setuju) {
    // Setuju → Dikerjakan: hold selalu dibersihkan; kerja 'hold'→'belum', 'proses' DIPERTAHANKAN (faithful persetujuanSave v0.18).
    const kerja = g.kerja === 'hold' ? 'belum' : (g.kerja ?? 'belum');
    await repo.advance(no, { reconfirm: false, hold: false, kerja }, 'Dikerjakan', `Pelanggan SETUJU${ket ? ': ' + ket : ''}`, by());
    await notifyNext(no, (m) => (m.teknisi ? { target: 'TEK:' + m.teknisi, text: `✅ ${no} DISETUJUI pelanggan — silakan kerjakan.` } : null));
  } else {
    await repo.advance(no, { hasil: 'batal', reconfirm: false, kondisi: 'Dibatalkan (pelanggan tidak setuju)' }, 'Siap Diambil', `Pelanggan TIDAK setuju: ${ket}`, by());
    // Paritas v0.18 (Batch 48): tidak-setuju → TEKNISI (hasil diagnosanya ditolak); fallback CS bila belum ada teknisi. CS tetap lihat unit di Perlu "siap".
    await notifyNext(no, (m) => (m.teknisi
      ? { target: 'TEK:' + m.teknisi, text: `🚫 ${no} hasil diagnosa DITOLAK pelanggan: ${ket}. Unit siap diambil.` }
      : { target: 'CS', text: `🚫 ${no} pelanggan TIDAK setuju: ${ket}. Siap diambil.` }));
  }
}

/** Dikerjakan → Siap Diambil (hasil). Notif → CS. */
export async function finishWork(
  no: string, jadi: boolean, biaya: number, modal: number, tindakan: string, garansi: string, ket: string,
  garansiBase?: string | null, refundUsul?: { amount: number; note: string; ronde: number } | null,
): Promise<void> {
  await assertAdvance(no, 'Dikerjakan');
  if (jadi) {
    // garansi_base DITULIS SELALU: warisan → base lama (freeze); fresh → null (pickup set tgl pickup).
    // Wajib unconditional agar base basi sesi lama tak nyangkut & bikin garansi fresh langsung kedaluwarsa.
    const patch: Record<string, unknown> = { hasil: 'jadi', biaya, modal, tindakan, garansi, garansi_base: garansiBase ?? null };
    await repo.advance(no, patch, 'Siap Diambil', `Selesai: ${tindakan}`, by());
  } else {
    // Gagal: pelanggan tak ditagih (biaya 0) TAPI modal sparepart yg sudah dipakai TETAP dihitung (HPP riil / rugi part).
    // Port v0.18 (kjSave batal pertahankan modal). `modal` = nilai sparepart terpakai (prefill recomputeModal).
    const patch: Record<string, unknown> = { hasil: 'tidakbisa', biaya: 0, modal, kondisi: 'Tidak bisa diperbaiki', garansi: 'Tidak Ada', garansi_base: null };
    if (refundUsul && refundUsul.amount > 0) patch.refund_usul = { amount: refundUsul.amount, note: refundUsul.note, ronde: refundUsul.ronde, by: by(), at: new Date().toISOString() };
    await repo.advance(no, patch, 'Siap Diambil', `Tidak bisa diperbaiki${ket ? ': ' + ket : ''}`, by());
  }
  await notifyNext(no, () => ({ target: 'CS', text: jadi ? `📦 ${no} SELESAI (${tindakan}) — siap diambil, hubungi pelanggan.` : `📦 ${no} tidak bisa diperbaiki — siap diambil, hubungi pelanggan.` }));
  // Usul refund (servis ulang gagal) → notif CS (proses) + Finance (verifikasi). Port R1 v0.18.
  if (!jadi && refundUsul && refundUsul.amount > 0) {
    const rpAmt = `Rp ${refundUsul.amount.toLocaleString('id-ID')}`;
    await notifyNext(no, () => ({ target: 'CS', tahap: null, text: `💸 Usul pengembalian dana ${no} ${rpAmt} (servis ulang gagal) — proses saat serah terima.` }));
    await notifyNext(no, () => ({ target: 'FINANCE', tahap: null, text: `💸 Usul pengembalian dana ${no} ${rpAmt} menunggu — verifikasi saat serah terima.` }));
  }
}

/** Estimasi biaya simpan saat ini (utk ditampilkan di dialog serah terima). */
export const storageFeePreview = (no: string): Promise<{ readyDays: number; fee: number }> => storageFeeNow(no);

/** Siap Diambil → Sudah Diambil (serah terima + pembayaran; verifikasi Finance menyusul).
 *  Biaya simpan (bila unit telat diambil) DIBEKUKAN ke tiket saat serah terima. */
export async function pickup(no: string, cara: string, lunas: boolean, pengambil: string, jatuhTempo?: string | null, garansiOverride?: string): Promise<void> {
  // Guard idempoten (port finalizeAmbil v0.18): jangan proses ulang bila sudah pindah.
  if ((await repo.currentStatus(no)) !== 'Siap Diambil') return;
  const cu = currentUser(); // RBAC (P1-c): serah terima = O/M/SPV/CS (canAdvance 'Siap Diambil')
  if (!cu || !['Owner', 'Manager', 'SPV', 'CS'].includes(cu.role)) throw new Error('Serah terima hanya oleh Owner/Manager/SPV/CS.');
  // SoD: peran yang boleh serah-terima (incl. SPV) TAPI tak boleh markLunas → paksa lunas=false (samakan domain+RLS 0050, cegah pickup gagal). Reviewer LOW.
  const lunasEff = canMarkLunas(cu.role) ? lunas : false;
  const tempo = isPiutangCara(cara); // Tempo (Toko) & Piutang sama-sama = belum dibayar → piutang
  const { fee } = await storageFeeNow(no);
  const existingBase = await repo.garansiBaseOf(no); // preserve base warisan servis-ulang; null → mulai hitung dari tgl pickup
  await repo.advance(
    no,
    { cara_bayar: cara, lunas: lunasEff, verified: false, pengambil, tgl_ambil_iso: todayISO(), garansi_base: existingBase ?? todayISO(), jatuh_tempo: tempo ? (jatuhTempo || null) : null, biaya_simpan: fee, ...(garansiOverride ? { garansi: garansiOverride } : {}) },
    'Sudah Diambil',
    `Diambil oleh ${pengambil} · ${cara}${tempo && jatuhTempo ? ` (jatuh tempo ${jatuhTempo})` : lunasEff ? ' · LUNAS (menunggu verifikasi Finance)' : ''}${fee > 0 ? ` · +biaya simpan ${rp(fee)}` : ''}`,
    by(),
  );
  await notifyNext(no, () => ({ target: 'FINANCE', tahap: null, text: tempo ? `📒 ${no} diambil TEMPO (jatuh tempo ${jatuhTempo ?? '-'}) — piutang, siap ditagih.` : `💳 ${no} diambil (${cara}${lunasEff ? ', LUNAS' : ''}) — perlu verifikasi Finance.` }));
  await notifyNext(no, (m) => (m.teknisi ? { target: 'TEK:' + m.teknisi, text: `📦 ${no} sudah diambil pelanggan.` } : null));
}

/** Tandai / batal "sudah konfirmasi ke pelanggan" (O/M/SPV/CS, status Menunggu Persetujuan). Port markKonf/unmarkKonf v0.18. */
export async function setKonfirmasi(no: string, val: boolean): Promise<void> {
  const u = currentUser();
  if (!u || !['Owner', 'Manager', 'SPV', 'CS'].includes(u.role)) throw new Error('Tidak punya akses konfirmasi.');
  if ((await repo.currentStatus(no)) !== 'Menunggu Persetujuan') throw new Error('Hanya untuk tiket Menunggu Persetujuan.');
  await repo.setKonfirmasi(no, val, u.nama);
}

/** Ubah prioritas tiket (O/M/SPV). */
export async function setPrioritas(no: string, prio: string): Promise<void> {
  const u = currentUser();
  if (!u || !['Owner', 'Manager', 'SPV'].includes(u.role)) throw new Error('Ubah prioritas hanya oleh Owner/Manager/SPV.');
  if (prio === 'P0' && !['Owner', 'Manager'].includes(u.role)) throw new Error('Prioritas P0 (darurat) hanya oleh Owner/Manager.');
  await repo.updateFields(no, { prioritas: prio }, `Prioritas → ${prio}`, 'Ubah prioritas', by());
}

/** Hold / lanjut (Menunggu Sparepart) saat Dikerjakan. RBAC: O/M/SPV atau teknisi pemilik. */
export async function setHold(no: string, hold: boolean, reason = ''): Promise<void> {
  await assertAdvance(no, 'Dikerjakan');
  await repo.updateFields(no, { hold, kerja: hold ? 'hold' : 'proses' },
    'Dikerjakan', hold ? `⏸️ Hold — ${reason || 'menunggu sparepart'}` : '▶️ Lanjut dikerjakan', by());
}

/** Konfirmasi ulang: tambah/kurang biaya → balik Menunggu Persetujuan. Notif → CS. */
export async function reconfirm(no: string, jenis: 'tambah' | 'kurang', nominal: number, oldEst: number, ket: string): Promise<void> {
  await assertAdvance(no, 'Dikerjakan'); // RBAC (P1-c konsistensi): O/M/SPV atau teknisi pemilik (yang menaikkan biaya)
  if (jenis === 'kurang' && nominal > oldEst) throw new Error('Pengurangan tidak boleh melebihi biaya lama.');
  const total = jenis === 'kurang' ? Math.max(0, oldEst - nominal) : oldEst + nominal;
  await repo.updateFields(
    no,
    { est_biaya: total, est_biaya_lama: oldEst, reconfirm: true, ket_ulang: ket, konfirmasi: false, status: 'Menunggu Persetujuan' },
    'Menunggu Persetujuan',
    `Konfirmasi ulang (${jenis} ${rp(nominal)}): total baru ${rp(total)} — ${ket}`,
    by(),
  );
  await notifyNext(no, () => ({ target: 'CS', text: `🔁 ${no} konfirmasi ulang (${jenis} ${rp(nominal)}) → total ${rp(total)}. Minta persetujuan pelanggan.` }));
}

// (§26 v20) Hard-delete tiket DIHAPUS — semua penghapusan lewat Delete-Approval (audit.service.requestDeleteTicket).

/** Batalkan langsung (dari status awal) → Siap Diambil, hasil batal. Notif → CS. RBAC: O/M/SPV/CS. */
export async function batalLangsung(no: string, alasan: string): Promise<void> {
  const u = currentUser();
  if (!u || !['Owner', 'Manager', 'SPV', 'CS'].includes(u.role)) throw new Error('Pembatalan hanya oleh Owner/Manager/SPV/CS.');
  const g = await repo.guardInfo(no);
  if (!['Diterima', 'Diagnosa', 'Menunggu Persetujuan'].includes(g.status)) throw new Error(`Tidak bisa batalkan langsung dari status "${g.status}".`);
  await repo.advance(no, { hasil: 'batal', kondisi: 'Dibatalkan' }, 'Siap Diambil', `Dibatalkan: ${alasan}`, by());
  await notifyNext(no, () => ({ target: 'CS', text: `❌ ${no} dibatalkan: ${alasan}. Siap diambil, hubungi pelanggan.` }));
}

/** Arsipkan transaksi (Owner) — hilang dari antrian operasional, data utuh. */
export async function archiveTicket(no: string): Promise<void> {
  const u = currentUser();
  if (!u || u.role !== 'Owner') throw new Error('Hanya Owner yang dapat mengarsipkan.');
  await repo.updateFields(no, { archived: true }, 'Arsip', 'Tiket diarsipkan', by());
  await logAudit({ action: 'ARCHIVE', module: 'ticket', refId: no });
}

/** Edit garansi tiket Sudah Diambil — jaga-jaga CS/teknisi lupa isi saat serah (Apo 19 Jul).
 *  Hanya O/M + alasan WAJIB; tercatat di histori tiket + audit log. Basis hitung (`garansi_base`)
 *  TIDAK digeser bila sudah ada — dan bila kosong diisi tgl AMBIL, bukan tgl edit, supaya
 *  mengedit belakangan tidak diam-diam memperpanjang masa garansi. */
export async function editGaransi(no: string, garansi: string, alasan: string): Promise<void> {
  const u = currentUser();
  if (!u || !['Owner', 'Manager'].includes(u.role)) throw new Error('Edit garansi hanya oleh Owner/Manager.');
  if (!alasan.trim()) throw new Error('Alasan wajib diisi.');
  if ((await repo.currentStatus(no)) !== 'Sudah Diambil') throw new Error('Hanya untuk tiket Sudah Diambil.');
  const t = await repo.garansiEditMeta(no);
  if (t.frozen) throw new Error('Tiket terarsip/void — kembalikan dulu dari menu Arsip.');
  const base = t.garansiBase ?? t.tglAmbilISO ?? todayISO();
  await repo.updateFields(no, { garansi, garansi_base: base }, 'Garansi',
    `Garansi diubah "${t.garansi ?? 'Tidak Ada'}" → "${garansi}" oleh ${u.role} — ${alasan.trim()}`, by());
  await logAudit({ action: 'GARANSI_EDIT', module: 'ticket', refId: no, reason: alasan.trim(), meta: { dari: t.garansi, ke: garansi, base } });
}

/** Servis Lagi (Garansi): garansi valid → servis-ulang di tempat (multi-sesi);
 *  garansi habis → tiket BARU berbayar (prior_ref). */
export async function servisLagi(
  no: string, garansi: string | null, garansiBase: string | null, outlet: string, keluhan: string,
): Promise<{ mode: 'ulang' | 'baru'; newNo?: string }> {
  const u = currentUser();
  if (!u) throw new Error('Sesi tidak valid.');
  if (!['Owner', 'Manager', 'SPV', 'CS'].includes(u.role)) throw new Error('Tidak punya akses memproses klaim ini.');
  // Anti-double (P1-e): hanya dari unit Siap/Sudah Diambil. Double-fire kedua → status sudah pindah → ditolak.
  const st = await repo.currentStatus(no);
  if (!['Siap Diambil', 'Sudah Diambil'].includes(st)) throw new Error('Servis lagi hanya dari unit Siap / Sudah Diambil.');
  // KLAIM GARANSI (garansi habis → tiket BARU) HANYA dari Sudah Diambil (unit sudah diambil pelanggan). Port v0.18:
  // dari Siap Diambil (belum diambil) → SELALU in-place (balik Dikerjakan), tak pernah bikin tiket baru.
  // (garansi_base baru di-set saat pickup; menilai "expired" utk tiket belum-diambil = keliru → tiket dangling.)
  if (st === 'Sudah Diambil' && garansiExpired(garansi, garansiBase)) {
    // Tiket lama tetap "Sudah Diambil" setelah tiket baru dibuat, jadi guard status di atas
    // TIDAK menjaga jalur ini — tombolnya bisa ditekan berkali-kali dan tiap kali melahirkan
    // tiket baru untuk satu HP yang sama (kejadian nyata: 2 tiket, selisih 56 detik).
    // Diperiksa per UNIT FISIK: HP yang sering servis punya banyak tiket lama, masing-masing
    // dengan tombolnya sendiri — menjaga per-tiket saja masih meloloskan kembar.
    const lanjutan = await repo.activeForUnit(no);
    if (lanjutan) throw new Error(`Unit ini sudah punya servis berjalan: ${lanjutan.no} (${lanjutan.status}). Lanjutkan tiket itu, jangan buat baru.`);
    const newNo = genNo(outlet);
    await repo.createFromPrior(no, newNo, genToken(), keluhan, todayISO(), u.nama);
    await notifyNext(newNo, () => ({ target: 'SPV', text: `🆕 Servis baru ${newNo} dari unit lama ${no} (garansi habis) — tugaskan teknisi.` }));
    return { mode: 'baru', newNo };
  }
  await repo.servisUlang(no, keluhan, u.nama);
  await notifyNext(no, (m) => (m.teknisi ? { target: 'TEK:' + m.teknisi, text: `🔄 ${no} dikembalikan untuk servis lagi (klaim garansi).` } : null));
  return { mode: 'ulang' };
}
